import { type Member } from '@/features/iam/members/Member'
import { type CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import type {
  FetchMembersByPublicIdsFn,
  PrimeMemberByPublicIdFn,
} from '@/features/iam'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { isNotNil } from '@/utils/object-util'
import { type Job } from './Job'
import { formatMemberOption } from '@/features/iam/members/member-display'
import { type ListQualifiedProvidersFn } from '@/features/opportunities'
import { ListPreviousProvidersRequest } from '@taxfyle/api-internal/internal/draft_pb'

/**
 * Query for the providers qualified for a job.
 */
export interface ListQualifiedProvidersQuery {
  job: Job
  searchText: string
  after?: string
}

/**
 * Provider Store.
 */
export class ProviderStore {
  constructor(
    private readonly _api: JobsApi,
    private readonly _primeMemberByPublicId: PrimeMemberByPublicIdFn,
    private readonly _fetchMembersByPublicIds: FetchMembersByPublicIdsFn,
    private readonly _listQualifiedProviders: ListQualifiedProvidersFn,
  ) {}

  /**
   * Fetches available providers for the specified workspace member
   * (must be the current user).
   *
   * @param member
   * @param signal
   */
  async fetchAvailableProvidersForMember(
    member: Member,
    signal?: AbortSignal,
  ): Promise<Member[]> {
    if (!member.isCurrentUser) {
      throw new Error('Cannot fetch previous providers for non-current user.')
    }

    const request = new ListPreviousProvidersRequest()
      .setWorkspaceId(Id.assert(member.workspace.id))
      .setUserPublicId(Id.assert(member.user.publicId))

    const response = await this._api.drafts.listPreviousProviders(request)
    signal?.throwIfAborted()

    return response
      .getPreviousProvidersList()
      .map((x) =>
        this._primeMemberByPublicId({
          userPublicId: x.getUserPublicId(),
          familyName: x.getFamilyName(),
          givenName: x.getGivenName(),
          picture: x.getPicture(),
          workspaceId: x.getWorkspaceId(),
          displayName: x.getDisplayName(),
        }),
      )
      .filter(isNotNil)
  }

  /**
   * Lists the providers qualified for a job whose name matches the search
   * text.
   */
  async listQualifiedProviders(
    { job, searchText, after }: ListQualifiedProvidersQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> {
    const lowerSearchText = searchText.toLowerCase()
    const page = await this._listQualifiedProviders(job.id, {
      limit: 25,
      after,
    })

    const memberRequests: Parameters<FetchMembersByPublicIdsFn>[0] =
      page.data.map((id) => ({
        workspaceId: Id.assert(job.workspace.id),
        userPublicId: Id.assert(id),
      }))

    const result = await this._fetchMembersByPublicIds(memberRequests)
    signal?.throwIfAborted()

    return {
      next: page.next,
      data: result
        .filter(isNotNil)
        .filter((m) =>
          formatMemberOption(m).toLowerCase().includes(lowerSearchText),
        ),
    }
  }
}
